// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { parseBoolean } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
    showSignOutLink = "showSignOutLink",
}

const enum linkFields {
    url = "url",
    linkText = "linkText",
    openInNewTab = "openInNewTab",
    addBottomBorder = "addBottomBorder",
}

type LinkModel = {
    fields: {
        url: LinkFieldValue;
        linkText: string;
        openInNewTab: boolean;
        addBottomBorder: boolean;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.showSignOutLink]: boolean;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const customSettings = getSettingsCollection(state).customSettings as any;
    const session = state.context.session;

    return {
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        isPunchOutSession: getIsPunchOutSession(state),
        currentPage: getCurrentPage(state),
        sentinelURL: customSettings?.sentinelHeaderUrl,
        thinkificlURL: customSettings?.thinkificHeaderUrl,
        isSentinelUser: parseBoolean(session?.properties?.isSentinelUser),
        isThinkificUser: parseBoolean(session?.properties?.isThinkificUser),
    };
};

interface ListStyleProp {}

const ListStyle = styled.ul<ListStyleProp>`
    li {
        list-style: none;
        margin-bottom: 10px;
    }
`;
const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};
export interface MyAccountNavigationListStyles {
    separator?: InjectableCss;
    wrapper?: InjectableCss;
    mobileNavWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const myAccountNavigationListStyles: MyAccountNavigationListStyles = {};

const styles = myAccountNavigationListStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const MyAccountNavigationList = (props: Props) => {
    const {
        fields,
        history,
        currentPage,
        isPunchOutSession,
        cancelPunchOut,
        signOut,
        sentinelURL,
        thinkificlURL,
        isSentinelUser,
        isThinkificUser,
    } = props;

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const customCssWrapper = {
        css: css`
            .navigation-list {
                .navigation-list-item {
                    list-style: none;
                    margin: 0;
                }
            }
            ${props?.fields?.customCSS}
        `,
    };

    const signOutElement = fields.showSignOutLink ? (
        <Link
            className="signout-clickable"
            data-test-selector="user_signout"
            aria-label="sign out"
            onClick={onSignOutHandler}
            {...styles.link}
        >
            {translate("Sign Out")}
        </Link>
    ) : null;
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.url);

    const handleSelectChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
        event.preventDefault();
        if (event.target.value === "signout") {
            onSignOutHandler();
            return;
        }
        const url = event.currentTarget.value;
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            window.open(url, "_blank");
        } else {
            history.push(url);
        }
    };

    if (!links) {
        <StyledWrapper {...customCssWrapper}>{signOutElement}</StyledWrapper>;
    }

    return (
        <StyledWrapper {...customCssWrapper}>
            <ListStyle className="navigation-list" data-test-selector="navigationList">
                {links.map((link, index) => {
                    const fieldLink = fields?.links?.[index]?.fields;
                    if (
                        (link.url === sentinelURL && !isSentinelUser) ||
                        (link.url === thinkificlURL && !isThinkificUser)
                    ) {
                        return null;
                    }

                    if ((link as { excludeFromNavigation?: boolean }).excludeFromNavigation) {
                        return null;
                    }

                    return (
                        <li key={link.url ?? index} className="navigation-list-item">
                            <Link
                                className="navigation-list-link"
                                data-test-selector={`navigationList-${link.title}`}
                                href={link.url}
                                target={fieldLink.openInNewTab ? "_blank" : ""}
                                {...styles.link}
                                aria-label={fieldLink.linkText || link.title}
                            >
                                {fieldLink.linkText || link.title}
                            </Link>
                            {fieldLink.addBottomBorder && <StyledWrapper {...styles.separator}></StyledWrapper>}
                        </li>
                    );
                })}
            </ListStyle>
            {signOutElement}
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
.signout-clickable {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyAccountNavigationList)),
    definition: {
        group: XNGAGE_ELEMENTS,
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                tab: basicTab,
                getDisplay: (item: HasFields, state) => {
                    const {
                        url: { type, value },
                        linkText,
                    } = item.fields;
                    if (linkText) {
                        return linkText;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                fieldDefinitions: [
                    {
                        name: linkFields.url,
                        editorTemplate: "LinkField",
                        isRequired: true,
                        defaultValue: {
                            type: "Page",
                            value: "",
                        },
                    },
                    {
                        name: linkFields.linkText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        validate: (value, item) => (value.length > 40 ? "max allowed characters is 40" : null),
                    },
                    {
                        name: linkFields.openInNewTab,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: linkFields.addBottomBorder,
                        displayName: "Add Bottom Border",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.showSignOutLink,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
